'use client';
import React from 'react';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faFileAlt } from '@fortawesome/pro-solid-svg-icons/faFileAlt';
import { Flex, Box } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';
import Image from 'next/image';

import styles from './ItemThumbnail.module.css';
import { Duration } from './Duration';
import ProgressBar from '../ProgressBar';
import { useIntl } from 'react-intl';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { bg0, bg5 } from '@osmosis/tailwind-ui/styles/background';

export type ThumbnailClassNames = {
  thumbnailContainer?: string;
  durationContainer?: string;
  durationBadge?: string;
  playIconContainer?: string;
  playIcon?: string;
};

export type ItemThumbnailProps = {
  thumbnailURL: string;
  altText?: string;
  playIcon: boolean;
  showDocIcon?: boolean;
  minHeight: string; // it's required prop since initialy component doesn't know about image size and by default it's 0
  permanentDuration?: boolean;
  duration: string | null;
  watchPercent?: number;
  imageSx?: React.CSSProperties;
  dataTestId?: string;
  title: string;
  unoptimized?: boolean;
  toBeRetired?: boolean;
  iconLabel?: string;
  archivedStatus?: boolean;
  progressHeight?: number;
  focusableThumbnail?: boolean;
  classNames?: ThumbnailClassNames;
};

/**
 * Returns ItemThumbnail component.
 */
const ItemThumbnail = (props: ItemThumbnailProps) => {
  const {
    thumbnailURL,
    altText = '',
    playIcon = true,
    showDocIcon,
    permanentDuration,
    minHeight,
    duration,
    watchPercent,
    imageSx,
    dataTestId,
    title,
    unoptimized = false,
    toBeRetired,
    iconLabel,
    archivedStatus,
    progressHeight = 4,
    classNames = {},
    focusableThumbnail = true,
  } = props;

  const colors = useColors();
  const { formatMessage } = useIntl();

  const [hideDuration, setHideDuration] = React.useState(false);

  return (
    <Flex
      data-testid={dataTestId ? `${dataTestId}-thumbnail` : 'thumbnail'}
      className={cn(
        `relative justify-center width-auto h-full min-h-[${minHeight}] border-2 border-blue-600 rounded-md overflow-hidden `,
        bg0,
        archivedStatus ? 'cursor-default grayscale' : 'hover:cursor-pointer',
        classNames.thumbnailContainer,
      )}
    >
      <Image
        className={`${styles.image} items-center justify-center object-contain`}
        width={200} // Magic numbers; 16 by 9 aspect ratio
        height={112.5}
        unoptimized={unoptimized}
        style={{
          ...imageSx,
        }}
        src={thumbnailURL}
        alt={altText}
        aria-label={formatMessage(
          {
            id: 'item.thumbnail.aria-label-title',
            defaultMessage: '{title}',
          },
          { title },
        )}
      />

      {/* The playIcon and duration can annoyingly overlap when there's a really small thumbnail.
          by transparentifying the duration when the video is hovered on, and setting it to 0px
          when it's hovered upon so the play icon still appears, it allows for the duration to
          never be in the way. */}
      {(playIcon || showDocIcon) && !toBeRetired && (
        <Box>
          <Flex
            data-testid="thumbnail-layout-with-opacity"
            className={cn(
              'absolute top-0 left-0 w-full h-full justify-center items-center transition ease-in-out delay-100 bg-black/20 rounded-md opacity-0 hover:opacity-100 focus:opacity-100',
              classNames.playIconContainer,
            )}
            onMouseEnter={() => setHideDuration(true)}
            onMouseLeave={() => setHideDuration(false)}
            onFocus={() => setHideDuration(true)}
            onBlur={() => setHideDuration(false)}
            tabIndex={focusableThumbnail ? 0 : -1}
          >
            <Box
              className={cn(
                `px-3 py-2 ${bg5} rounded-lg opacity-100`,
                classNames.playIcon,
              )}
            >
              <Icon
                icon={playIcon ? faPlay : faFileAlt}
                color={colors.white}
                aria-label={iconLabel}
                aria-hidden="false"
              />
            </Box>
          </Flex>
        </Box>
      )}

      {duration ? (
        <Box
          className={cn(
            `absolute bottom-[10%] right-[5%] transition-opacity ease-in-out delay-100`,
            hideDuration && !permanentDuration
              ? 'opacity-0 invisible'
              : 'opacity-100 visible',
            classNames.durationContainer,
          )}
        >
          <Duration duration={duration} className={classNames.durationBadge} />
        </Box>
      ) : null}

      {watchPercent ? (
        <Box className="absolute bottom-0 w-full">
          <ProgressBar
            height={progressHeight}
            barColor="#FF0000"
            progress={watchPercent}
          />
        </Box>
      ) : null}
    </Flex>
  );
};

export default ItemThumbnail;
